/**
 * HOUSEHOLD SETUP v2.1 - CRASH PROOF
 * ✅ Optional Chaining (?.) en user y userProfile para evitar crashes de Sentry
 * ✅ useCallback para handlers
 * ✅ React.memo en componentes
 * ✅ useMemo para configuración
 * ✅ Mobile optimizations
 */
import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFirebase } from '../context/FirebaseContext';
import { createHousehold, joinHousehold, leaveHousehold } from '../services/firestore';

const HouseholdSetup = memo(function HouseholdSetup({ isOpen, onClose }) {
  const { user, refreshUserProfile } = useAuth();
  const { syncToFirebase, household, userProfile } = useFirebase();
   
  const [mode, setMode] = useState('choose');
  const [householdName, setHouseholdName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // ✅ OPTIMIZADO: Detectar automáticamente qué pantalla mostrar
  useEffect(() => {
    if (isOpen) {
      if (userProfile?.householdId) {
        setMode('view');
      } else {
        setMode('choose');
      }
    }
  }, [isOpen, userProfile]);

  // ✅ OPTIMIZADO: useCallback para handlers (Protegido con ?.)
  const handleCreate = useCallback(async () => {
    if (!householdName.trim()) {
      setError('Ingresa un nombre para tu hogar');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await createHousehold(user?.uid, householdName);
      
      if (result.success) {
        setGeneratedCode(result.inviteCode);
        await refreshUserProfile();
        await syncToFirebase();
        setMode('created');
      } else {
        setError(result.error || 'Error al crear el hogar');
      }
    } catch (err) {
      setError('Error inesperado al crear el hogar');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [householdName, user?.uid, refreshUserProfile, syncToFirebase]);

  const handleJoin = useCallback(async () => {
    if (!inviteCode.trim()) {
      setError('Ingresa el código de invitación');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await joinHousehold(user?.uid, inviteCode);
      
      if (result.success) {
        await refreshUserProfile();
        onClose();
        setTimeout(() => {
          window.location.reload();
        }, 300);
      } else {
        setError(result.error || 'Error al unirse al hogar');
      }
    } catch (err) {
      setError('Error inesperado al unirse al hogar');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [inviteCode, user?.uid, refreshUserProfile, onClose]);

  const handleLeaveHousehold = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // ✅ ACÁ ESTABA EL ERROR DE SENTRY: Protegido con ?.
      const result = await leaveHousehold(user?.uid, userProfile?.householdId);
      
      if (result.success) {
        await refreshUserProfile();
        setShowLeaveConfirm(false);
        setMode('choose');
        
        if (window.confirm('¿Querés limpiar tus datos locales (stock, recetas, listas)?')) {
          localStorage.clear();
          window.location.reload();
        }
      } else {
        setError(result.error || 'Error al abandonar el hogar');
      }
    } catch (err) {
      setError('Error inesperado al abandonar el hogar');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, userProfile?.householdId, refreshUserProfile]);

  const resetAndClose = useCallback(() => {
    setHouseholdName('');
    setInviteCode('');
    setGeneratedCode('');
    setError('');
    setShowLeaveConfirm(false);
    onClose();
  }, [onClose]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      resetAndClose();
    }
  }, [resetAndClose]);

  const handleCopyCode = useCallback((code, event) => {
    navigator.clipboard.writeText(code);
    const btn = event.target;
    const originalText = btn.textContent;
    btn.textContent = '✓ ¡Copiado!';
    setTimeout(() => {
      btn.textContent = originalText;
    }, 2000);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />
      
      <div className="relative bg-gradient-to-br from-white to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-8 max-w-md w-full shadow-2xl border-2 border-gray-200 dark:border-gray-700/50">
        
        <CloseButton onClick={resetAndClose} />

        {error && (
          <ErrorAlert error={error} onDismiss={() => setError('')} />
        )}

        {mode === 'view' && household && !showLeaveConfirm && (
          <ViewHouseholdMode
            household={household}
            onCopyCode={handleCopyCode}
            onClose={resetAndClose}
            onLeave={() => setShowLeaveConfirm(true)}
          />
        )}

        {mode === 'view' && showLeaveConfirm && (
          <LeaveConfirmMode
            loading={loading}
            onConfirm={handleLeaveHousehold}
            onCancel={() => setShowLeaveConfirm(false)}
          />
        )}

        {mode === 'choose' && !userProfile?.householdId && (
          <ChooseMode
            onCreateClick={() => {
              setMode('create');
              setError('');
            }}
            onJoinClick={() => {
              setMode('join');
              setError('');
            }}
          />
        )}

        {mode === 'create' && (
          <CreateMode
            householdName={householdName}
            loading={loading}
            onNameChange={setHouseholdName}
            onCreate={handleCreate}
            onBack={() => {
              setMode('choose');
              setError('');
            }}
          />
        )}

        {mode === 'created' && (
          <CreatedMode
            generatedCode={generatedCode}
            onCopyCode={handleCopyCode}
            onContinue={resetAndClose}
          />
        )}

        {mode === 'join' && (
          <JoinMode
            inviteCode={inviteCode}
            loading={loading}
            onCodeChange={(value) => setInviteCode(value.toUpperCase())}
            onJoin={handleJoin}
            onBack={() => {
              setMode('choose');
              setError('');
            }}
          />
        )}
      </div>
    </div>
  );
});

// ✅ COMPONENTES MEMOIZADOS

const CloseButton = memo(function CloseButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition active:scale-95"
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
});

const ErrorAlert = memo(function ErrorAlert({ error, onDismiss }) {
  return (
    <div className="mb-4 bg-red-900/30 border border-red-500/50 rounded-lg p-3 flex items-start gap-2">
      <span className="text-red-400 text-lg">⚠️</span>
      <p className="text-red-300 text-sm flex-1">{error}</p>
      <button
        onClick={onDismiss}
        className="text-red-400 hover:text-red-300 active:scale-95"
      >
        ✕
      </button>
    </div>
  );
});

const BackButton = memo(function BackButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="mb-4 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition flex items-center gap-2 active:scale-95"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      <span>Volver</span>
    </button>
  );
});

// ✅ COMPONENTES DE MODOS ESPECÍFICOS

const ViewHouseholdMode = memo(function ViewHouseholdMode({
  household,
  onCopyCode,
  onClose,
  onLeave
}) {
  return (
    <div>
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
          <span className="text-3xl">🏠</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {household.name}
        </h2>
        <p className="text-gray-400 text-sm">
          {household.members?.length || 1} {household.members?.length === 1 ? 'miembro' : 'miembros'}
        </p>
      </div>

      <div className="bg-white/80 dark:bg-gray-700/50 backdrop-blur-sm rounded-xl p-6 mb-4 border-2 border-gray-300 dark:border-gray-600/50">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 text-center">Código de invitación</p>
        <p className="text-4xl font-bold text-center text-green-400 tracking-wider mb-4">
          {household.inviteCode}
        </p>
        <button
          onClick={(e) => onCopyCode(household.inviteCode, e)}
          className="w-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-900 dark:text-white py-2 rounded-lg text-sm transition active:scale-95"
        >
          📋 Copiar código
        </button>
      </div>

      <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4 mb-4">
        <p className="text-blue-300 text-sm">
          💡 <strong>Tip:</strong> Comparte este código con tu pareja para que puedan sincronizar sus listas y planificación en tiempo real.
        </p>
      </div>

      <div className="space-y-2">
        <button
          onClick={onClose}
          className="w-full bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-semibold py-3 px-4 rounded-xl transition shadow-lg hover:shadow-xl active:scale-95"
        >
          Cerrar
        </button>
        
        <button
          onClick={onLeave}
          className="w-full bg-red-900/30 hover:bg-red-900/50 border-2 border-red-500/50 hover:border-red-500 text-red-400 hover:text-red-300 font-medium py-3 px-4 rounded-xl transition active:scale-95"
        >
          🚪 Abandonar hogar
        </button>
      </div>
    </div>
  );
});

const LeaveConfirmMode = memo(function LeaveConfirmMode({
  loading,
  onConfirm,
  onCancel
}) {
  return (
    <div>
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/30">
          <span className="text-3xl">⚠️</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          ¿Abandonar hogar?
        </h2>
        <p className="text-gray-400 text-sm">
          Esta acción no se puede deshacer
        </p>
      </div>

      <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 mb-6">
        <p className="text-red-300 text-sm mb-2">
          <strong>⚠️ Importante:</strong>
        </p>
        <ul className="text-red-300 text-sm space-y-1 list-disc list-inside">
          <li>Perderás acceso a los datos compartidos</li>
          <li>Tus datos locales se mantendrán en este dispositivo</li>
          <li>Podrás unirte a otro hogar o crear uno nuevo</li>
        </ul>
      </div>

      <div className="space-y-2">
        <button
          onClick={onConfirm}
          disabled={loading}
          className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold py-3 px-4 rounded-xl transition shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
        >
          {loading ? 'Abandonando...' : 'Sí, abandonar hogar'}
        </button>
        
        <button
          onClick={onCancel}
          className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-xl transition active:scale-95"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
});

const ChooseMode = memo(function ChooseMode({ onCreateClick, onJoinClick }) {
  return (
    <div>
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30">
          <span className="text-3xl">🏠</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Configura tu hogar
        </h2>
        <p className="text-gray-400 text-sm">
          Crea un nuevo hogar o únete a uno existente
        </p>
      </div>

      <div className="space-y-3">
        <button
          onClick={onCreateClick}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-4 rounded-xl transition shadow-lg hover:shadow-xl flex items-center justify-center gap-3 active:scale-95"
        >
          <span className="text-xl">+</span>
          <span>Crear nuevo hogar</span>
        </button>

        <button
          onClick={onJoinClick}
          className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-4 px-4 rounded-xl transition shadow-lg hover:shadow-xl flex items-center justify-center gap-3 active:scale-95"
        >
          <span className="text-xl">🔗</span>
          <span>Unirme con código</span>
        </button>
      </div>
    </div>
  );
});

const CreateMode = memo(function CreateMode({
  householdName,
  loading,
  onNameChange,
  onCreate,
  onBack
}) {
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !loading) {
      onCreate();
    }
  }, [onCreate, loading]);

  return (
    <div>
      <BackButton onClick={onBack} />

      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Crear nuevo hogar
        </h2>
        <p className="text-gray-400 text-sm">
          Dale un nombre a tu hogar compartido
        </p>
      </div>

      <input
        type="text"
        placeholder="Ej: Casa Martín y Ana"
        value={householdName}
        onChange={(e) => onNameChange(e.target.value)}
        onKeyPress={handleKeyPress}
        className="w-full bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-400/50 focus:outline-none mb-4"
        style={{ fontSize: '16px' }}
        disabled={loading}
        autoComplete="off"
        autoCorrect="off"
        spellCheck="false"
      />

      <button
        onClick={onCreate}
        disabled={loading}
        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-4 rounded-xl transition shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
      >
        {loading ? 'Creando...' : 'Crear hogar'}
      </button>
    </div>
  );
});

const CreatedMode = memo(function CreatedMode({
  generatedCode,
  onCopyCode,
  onContinue
}) {
  return (
    <div>
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
          <span className="text-3xl">✓</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          ¡Hogar creado!
        </h2>
        <p className="text-gray-400 text-sm">
          Comparte este código con tu pareja
        </p>
      </div>

      <div className="bg-white/80 dark:bg-gray-700/50 backdrop-blur-sm rounded-xl p-6 mb-6 border-2 border-gray-300 dark:border-gray-600/50">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 text-center">Código de invitación</p>
        <p className="text-4xl font-bold text-center text-blue-400 tracking-wider mb-4">
          {generatedCode}
        </p>
        <button
          onClick={(e) => onCopyCode(generatedCode, e)}
          className="w-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-900 dark:text-white py-2 rounded-lg text-sm transition active:scale-95"
        >
          📋 Copiar código
        </button>
      </div>

      <button
        onClick={onContinue}
        className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-3 px-4 rounded-xl transition shadow-lg hover:shadow-xl active:scale-95"
      >
        Continuar
      </button>
    </div>
  );
});

const JoinMode = memo(function JoinMode({
  inviteCode,
  loading,
  onCodeChange,
  onJoin,
  onBack
}) {
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !loading) {
      onJoin();
    }
  }, [onJoin, loading]);

  return (
    <div>
      <BackButton onClick={onBack} />

      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Unirse a un hogar
        </h2>
        <p className="text-gray-400 text-sm">
          Ingresa el código que te compartieron
        </p>
      </div>

      <input
        type="text"
        placeholder="Ej: ABC123"
        value={inviteCode}
        onChange={(e) => onCodeChange(e.target.value)}
        onKeyPress={handleKeyPress}
        className="w-full bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 text-gray-900 dark:text-white text-center text-2xl font-bold tracking-wider focus:border-purple-500 dark:focus:border-purple-400 focus:ring-2 focus:ring-purple-500/50 dark:focus:ring-purple-400/50 focus:outline-none mb-4"
        style={{ fontSize: '24px' }}
        maxLength={6}
        disabled={loading}
        autoComplete="off"
        autoCorrect="off"
        spellCheck="false"
      />

      <button
        onClick={onJoin}
        disabled={loading}
        className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-3 px-4 rounded-xl transition shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
      >
        {loading ? 'Uniéndome...' : 'Unirse al hogar'}
      </button>
    </div>
  );
});

export default HouseholdSetup;
